import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCloseButton,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSwitch,
  CFormTextarea,
  CInputGroup,
  CLoadingButton,
  CMultiSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
} from '@coreui/react-pro'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ToastComponent from 'src/components/common/TaostComponent'
import LeagueService from 'src/service/LeagueService'
import { LEAGUE_TYPE, leagueTeamIds } from './leagueHelpers'

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('League name is required').max(60, '60 character limit.'),
  description: Yup.string().max(250, '250 character limit.'),
  join_code: Yup.string().when('type', {
    is: LEAGUE_TYPE.PRIVATE,
    then: (schema) =>
      schema
        .required('Join code is required')
        .matches(/^[A-Z0-9]{4,10}$/, 'Use 4 to 10 letters or numbers.'),
  }),
})

// Create/edit a public or private league. Private leagues need a join code:
// a backend-generated one is pre-filled, and the admin can generate another
// or type their own. The backend checks the code is unique.
const LeagueForm = ({ visible, league, defaultType = LEAGUE_TYPE.PUBLIC, onClose, onSaved }) => {
  const [loader, setLoader] = useState(false)
  const [codeLoading, setCodeLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isTeamGame, setIsTeamGame] = useState(false)
  const [teams, setTeams] = useState([])
  const isEdit = !!league?.id

  const formik = useFormik({
    initialValues: {
      type: isEdit ? Number(league.type) : defaultType,
      name: league?.name || '',
      description: league?.description || '',
      join_code: league?.join_code || '',
      is_open: league ? !!league.is_open : true,
      team_ids: leagueTeamIds(league),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        type: values.type,
        name: values.name.trim(),
        description: values.description,
        is_open: values.is_open,
      }
      if (values.type === LEAGUE_TYPE.PRIVATE) payload.join_code = values.join_code
      if (isTeamGame) payload.team_ids = values.team_ids
      if (isEdit) payload.id = league.id

      setLoader(true)
      LeagueService.saveLeague(payload)
        .then((res) => {
          if (res.success) {
            ToastComponent(res.message, 'success')
            onSaved?.(res.data)
            handleClose()
          } else {
            setErrorMessage(res.message || 'Something went wrong.')
          }
        })
        .catch(() => ToastComponent('Something went wrong. Please try again.', 'error'))
        .finally(() => setLoader(false))
    },
  })

  const isPrivate = formik.values.type === LEAGUE_TYPE.PRIVATE
  const { setFieldValue } = formik

  const generateCode = () => {
    setCodeLoading(true)
    LeagueService.generateLeagueCode()
      .then((res) => res.success && setFieldValue('join_code', res.data.code))
      .finally(() => setCodeLoading(false))
  }

  // League based games (game type 2/3) can tag a league with teams.
  useEffect(() => {
    if (!visible) return
    LeagueService.getLeagueTeams().then((res) => {
      if (res.success) {
        setIsTeamGame(!!res.is_team_game)
        setTeams(res.data || [])
      }
    })
  }, [visible])

  // Built once per open so CMultiSelect keeps its own selection state.
  const teamOptions = useMemo(() => {
    const selectedIds = leagueTeamIds(league)
    return teams.map((team) => ({
      value: Number(team.id),
      text: team.name,
      selected: selectedIds.includes(Number(team.id)),
    }))
  }, [teams, league])

  // Pre-fill a code the first time a new private league is chosen.
  useEffect(() => {
    if (visible && isPrivate && !formik.values.join_code) generateCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isPrivate])

  const handleClose = () => {
    formik.resetForm()
    setErrorMessage('')
    onClose()
  }

  const title = `${isEdit ? 'Edit' : 'Create'} ${isPrivate ? 'Private' : 'Public'} League`

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 520 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>{title}</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            {isPrivate
              ? 'Members join this league by entering its code.'
              : 'Members find this league in the public list and join with one tap.'}
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody>
        {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
        <CForm className="row g-3" onSubmit={formik.handleSubmit}>
          {!isEdit && (
            <CCol xs={12}>
              <CFormLabel className="d-block">League Type</CFormLabel>
              <CButtonGroup role="group" aria-label="League type">
                <CButton
                  color="primary"
                  variant={isPrivate ? 'outline' : undefined}
                  onClick={() => setFieldValue('type', LEAGUE_TYPE.PUBLIC)}
                >
                  Public
                </CButton>
                <CButton
                  color="primary"
                  variant={isPrivate ? undefined : 'outline'}
                  onClick={() => setFieldValue('type', LEAGUE_TYPE.PRIVATE)}
                >
                  Private
                </CButton>
              </CButtonGroup>
            </CCol>
          )}
          <CCol xs={12}>
            <CFormLabel htmlFor="name">League Name *</CFormLabel>
            <CFormInput
              id="name"
              name="name"
              maxLength={60}
              placeholder={isPrivate ? 'e.g. The Clubhouse' : 'e.g. Gisborne'}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.errors.name && formik.touched.name)}
            />
            {formik.errors.name && formik.touched.name && (
              <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="description">Description</CFormLabel>
            <CFormTextarea
              id="description"
              name="description"
              rows={3}
              maxLength={250}
              placeholder="Optional short explanation"
              value={formik.values.description}
              onChange={formik.handleChange}
            />
            <div className="small text-medium-emphasis text-end">
              {formik.values.description.length}/250
            </div>
          </CCol>
          {isPrivate && (
            <CCol xs={12}>
              <CFormLabel htmlFor="join_code">Join Code *</CFormLabel>
              <CInputGroup>
                <CFormInput
                  id="join_code"
                  name="join_code"
                  maxLength={10}
                  className="font-monospace fw-semibold"
                  value={formik.values.join_code}
                  onChange={(e) =>
                    setFieldValue(
                      'join_code',
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                    )
                  }
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.errors.join_code && formik.touched.join_code)}
                />
                <CLoadingButton
                  color="secondary"
                  variant="outline"
                  loading={codeLoading}
                  onClick={generateCode}
                >
                  Generate new
                </CLoadingButton>
              </CInputGroup>
              {formik.errors.join_code && formik.touched.join_code ? (
                <div className="small text-danger mt-1">{formik.errors.join_code}</div>
              ) : (
                <div className="small text-medium-emphasis mt-1">
                  Use the generated code or type your own (4 to 10 letters or numbers).
                </div>
              )}
            </CCol>
          )}
          {isTeamGame && (
            <CCol xs={12}>
              <CFormLabel>Teams</CFormLabel>
              <CMultiSelect
                options={teamOptions}
                selectionType="tags"
                placeholder="Select one or more teams"
                searchNoResultsLabel="No teams found"
                onChange={(selected) =>
                  setFieldValue(
                    'team_ids',
                    Array.isArray(selected) ? selected.map((option) => option.value) : [],
                  )
                }
              />
              <div className="small text-medium-emphasis mt-1">
                Optional. Shown with the league so members can find their team&apos;s league.
              </div>
            </CCol>
          )}
          <CCol xs={12}>
            <CFormSwitch
              id="is_open"
              label="Open for joining"
              checked={formik.values.is_open}
              onChange={(e) => setFieldValue('is_open', e.target.checked)}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={formik.handleSubmit}>
          {isEdit ? 'Save Changes' : 'Create League'}
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default LeagueForm
